/**
 * Artifact Signing — Cryptographic Pipeline Artifact Protection
 * ==============================================================
 * Closes the mid-pipeline TOCTOU gap where artifacts written by one otter
 * can be tampered with before the next otter reads them.
 *
 * integrity.ts handles SHA-256 pinning of otter *definition* files at startup.
 * This module protects pipeline *artifacts* (`.stackwright/artifacts/`) with
 * ECDSA P-384 signatures. Ephemeral key pairs are per-pipeline-run, destroyed
 * after completion.
 */
import {
  createHash,
  generateKeyPairSync,
  createPublicKey,
  createPrivateKey,
  sign,
  verify,
  timingSafeEqual,
  KeyObject,
} from 'crypto';
import {
  readFileSync,
  writeFileSync,
  existsSync,
  mkdirSync,
  lstatSync,
  unlinkSync,
  readdirSync,
} from 'fs';
import { join } from 'path';
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ALGORITHM = 'ECDSA-P384-SHA384' as const;
const KEY_FILE = 'pipeline-keys.json';
const KEY_DIR = '.stackwright';
const SIGNATURE_MANIFEST = 'signatures.json';
const ARTIFACTS_DIR = '.stackwright/artifacts';

// ---------------------------------------------------------------------------
// Exported types & interfaces
// ---------------------------------------------------------------------------

/** Signature record for a single artifact. */
export interface ArtifactSignature {
  digest: string;
  signature: string;
  algorithm: typeof ALGORITHM;
  signedAt: string;
}

/** Manifest entry — extends ArtifactSignature with signer identity. */
export interface ManifestEntry extends ArtifactSignature {
  signedBy: string;
}

/** On-disk shape of `.stackwright/artifacts/signatures.json`. */
export interface SignatureManifest {
  version: '1.0';
  algorithm: typeof ALGORITHM;
  signatures: Record<string, ManifestEntry>;
}

/** On-disk shape of `.stackwright/pipeline-keys.json`. */
export interface PipelineKeyFile {
  version: '1.0';
  algorithm: typeof ALGORITHM;
  createdAt: string;
  publicKeyPem: string;
  privateKeyPem: string;
}

/** Structured audit event for a signature failure. */
export interface SignatureAuditEvent {
  artifactFilename: string;
  expectedDigest: string;
  actualDigest: string;
  phase: string;
  timestamp: string;
  source: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Reject symlinks at a given path. No-op if path doesn't exist. */
function rejectSymlink(filePath: string, context: string): void {
  if (!existsSync(filePath)) return;
  const stat = lstatSync(filePath);
  if (stat.isSymbolicLink()) {
    throw new Error(`Security: refusing to follow symlink at ${context}: ${filePath}`);
  }
}

/** Hex-encoded SHA-384 digest of raw bytes. Pure, no I/O. */
function computeSha384(data: Buffer): string {
  return createHash('sha384').update(data).digest('hex');
}

/** Constant-time comparison of two hex digest strings. */
function safeDigestEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  return timingSafeEqual(Buffer.from(a, 'utf8'), Buffer.from(b, 'utf8'));
}

/** Build an empty manifest with the correct shape. */
function emptyManifest(): SignatureManifest {
  return {
    version: '1.0',
    algorithm: ALGORITHM,
    signatures: {},
  };
}

// ---------------------------------------------------------------------------
// 1. Ephemeral Key Pair Management (ECDSA P-384)
// ---------------------------------------------------------------------------

/** Generate an ECDSA P-384 key pair, write to `.stackwright/pipeline-keys.json`. */
export function initPipelineKeys(cwd: string): { publicKeyPem: string; fingerprint: string } {
  const keyDir = join(cwd, KEY_DIR);
  const keyPath = join(keyDir, KEY_FILE);

  // Reject symlinks on the key file path
  rejectSymlink(keyPath, 'pipeline-keys.json');

  // Ensure directory exists
  mkdirSync(keyDir, { recursive: true });

  // Generate ECDSA P-384 key pair
  const { publicKey, privateKey } = generateKeyPairSync('ec', {
    namedCurve: 'P-384',
  });

  const publicKeyPem = publicKey.export({ type: 'spki', format: 'pem' }) as string;
  const privateKeyPem = privateKey.export({ type: 'pkcs8', format: 'pem' }) as string;

  // SHA-256 fingerprint of the public key PEM
  const fingerprint = createHash('sha256').update(publicKeyPem).digest('hex');

  const keyFile: PipelineKeyFile = {
    version: '1.0',
    algorithm: ALGORITHM,
    createdAt: new Date().toISOString(),
    publicKeyPem,
    privateKeyPem,
  };

  writeFileSync(keyPath, JSON.stringify(keyFile, null, 2), { encoding: 'utf-8' });

  return { publicKeyPem, fingerprint };
}

/** Load pipeline key pair from disk. Validates PEM, rejects symlinks. */
export function loadPipelineKeys(cwd: string): { privateKey: KeyObject; publicKey: KeyObject } {
  const keyPath = join(cwd, KEY_DIR, KEY_FILE);

  // Reject symlinks
  rejectSymlink(keyPath, 'pipeline-keys.json');

  if (!existsSync(keyPath)) {
    throw new Error('Pipeline keys not found — call initPipelineKeys() first');
  }

  let raw: string;
  try {
    raw = readFileSync(keyPath, 'utf-8');
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Cannot read pipeline keys: ${msg}`, { cause: err });
  }

  let parsed: PipelineKeyFile;
  try {
    parsed = JSON.parse(raw) as PipelineKeyFile;
  } catch {
    throw new Error('Pipeline keys file is not valid JSON');
  }

  // Basic PEM format validation
  if (
    typeof parsed.publicKeyPem !== 'string' ||
    !parsed.publicKeyPem.includes('-----BEGIN PUBLIC KEY-----')
  ) {
    throw new Error('Invalid public key PEM in pipeline keys file');
  }
  if (typeof parsed.privateKeyPem !== 'string' || !parsed.privateKeyPem.includes('-----BEGIN')) {
    throw new Error('Invalid private key PEM in pipeline keys file');
  }

  const publicKey = createPublicKey(parsed.publicKeyPem);
  const privateKey = createPrivateKey(parsed.privateKeyPem);

  return { privateKey, publicKey };
}

/** Overwrite private key material with zeros then delete. Idempotent. */
export function destroyPipelineKeys(cwd: string): void {
  const keyPath = join(cwd, KEY_DIR, KEY_FILE);

  if (!existsSync(keyPath)) return;

  // Reject symlinks even during destruction
  rejectSymlink(keyPath, 'pipeline-keys.json (destroy)');

  // Read → overwrite private key material with zeros → delete
  try {
    const raw = readFileSync(keyPath, 'utf-8');
    const parsed = JSON.parse(raw) as PipelineKeyFile;
    parsed.privateKeyPem = '0'.repeat(parsed.privateKeyPem.length);
    parsed.publicKeyPem = '0'.repeat(parsed.publicKeyPem.length);
    writeFileSync(keyPath, JSON.stringify(parsed, null, 2), { encoding: 'utf-8' });
  } catch {
    // Best-effort overwrite — proceed to delete regardless
  }

  try {
    unlinkSync(keyPath);
  } catch {
    // Already gone — idempotent
  }
}

// ---------------------------------------------------------------------------
// 2. Artifact Signing
// ---------------------------------------------------------------------------

/** Sign artifact bytes with ECDSA P-384. Returns structured signature record. */
export function signArtifact(artifactBytes: Buffer, privateKey: KeyObject): ArtifactSignature {
  const digest = computeSha384(artifactBytes);
  const sig = sign('SHA384', artifactBytes, privateKey);

  return {
    digest,
    signature: sig.toString('base64'),
    algorithm: ALGORITHM,
    signedAt: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// 3. Artifact Verification
// ---------------------------------------------------------------------------

/** Verify ECDSA signature AND constant-time SHA-384 digest. Both must pass. */
export function verifyArtifact(
  artifactBytes: Buffer,
  signature: ArtifactSignature,
  publicKey: KeyObject
): boolean {
  // Layer 1: ECDSA signature verification
  const sigValid = verify(
    'SHA384',
    artifactBytes,
    publicKey,
    Buffer.from(signature.signature, 'base64')
  );

  if (!sigValid) return false;

  // Layer 2: Constant-time digest comparison
  const actualDigest = computeSha384(artifactBytes);
  return safeDigestEqual(actualDigest, signature.digest);
}

// ---------------------------------------------------------------------------
// 4. Signature Manifest Management
// ---------------------------------------------------------------------------

/** Load signature manifest. Returns empty manifest if file doesn't exist. */
export function loadSignatureManifest(cwd: string): SignatureManifest {
  const manifestPath = join(cwd, ARTIFACTS_DIR, SIGNATURE_MANIFEST);

  rejectSymlink(manifestPath, 'signatures.json');

  if (!existsSync(manifestPath)) return emptyManifest();

  let raw: string;
  try {
    raw = readFileSync(manifestPath, 'utf-8');
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Cannot read signature manifest: ${msg}`, { cause: err });
  }

  try {
    const parsed = JSON.parse(raw) as SignatureManifest;
    // Basic shape validation — don't trust what's on disk blindly
    if (parsed.version !== '1.0' || typeof parsed.signatures !== 'object') {
      throw new Error('Malformed signature manifest: invalid version or missing signatures object');
    }
    return parsed;
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('Malformed')) throw err;
    throw new Error('Signature manifest is not valid JSON', { cause: err });
  }
}

/** Save a signature entry for a specific artifact. Read-modify-write. */
export function saveArtifactSignature(
  cwd: string,
  artifactFilename: string,
  sig: ArtifactSignature,
  signerOtter: string
): void {
  const artifactsDir = join(cwd, ARTIFACTS_DIR);
  const manifestPath = join(artifactsDir, SIGNATURE_MANIFEST);

  rejectSymlink(manifestPath, 'signatures.json (save)');

  // Ensure artifacts directory exists
  mkdirSync(artifactsDir, { recursive: true });

  const manifest = loadSignatureManifest(cwd);

  manifest.signatures[artifactFilename] = {
    ...sig,
    signedBy: signerOtter,
  };

  writeFileSync(manifestPath, JSON.stringify(manifest, null, 2), { encoding: 'utf-8' });
}

/** Retrieve signature for a specific artifact, or null if absent. */
export function getArtifactSignature(
  cwd: string,
  artifactFilename: string
): ArtifactSignature | null {
  const manifest = loadSignatureManifest(cwd);
  const entry = manifest.signatures[artifactFilename];
  if (!entry) return null;

  // Return just the ArtifactSignature fields (strip signedBy)
  return {
    digest: entry.digest,
    signature: entry.signature,
    algorithm: entry.algorithm,
    signedAt: entry.signedAt,
  };
}

// ---------------------------------------------------------------------------
// 5. Audit Events — SOC2 / FedRAMP / DoD ATO compliance
// ---------------------------------------------------------------------------

/**
 * Emit a structured ARTIFACT_SIGNATURE_FAIL audit event to stderr.
 *
 * Line prefix allows log shippers (FluentBit, syslog, CloudWatch, Splunk)
 * to route events via simple string filter before JSON parsing.
 */
export function emitSignatureAuditEvent(params: SignatureAuditEvent): void {
  const record = JSON.stringify({
    level: 'AUDIT',
    event: 'ARTIFACT_SIGNATURE_FAIL',
    timestamp: params.timestamp,
    source: params.source,
    artifactFilename: params.artifactFilename,
    expectedDigest: params.expectedDigest,
    actualDigest: params.actualDigest,
    phase: params.phase,
  });
  process.stderr.write(`ARTIFACT_SIGNATURE_FAIL ${record}\n`);
}

// ---------------------------------------------------------------------------
// 6. MCP Tool Registration
// ---------------------------------------------------------------------------

/** Per-artifact verification result returned by the MCP tool. */
export interface ArtifactVerificationResult {
  filename: string;
  verified: boolean;
  error?: string;
  signedBy?: string;
  signedAt?: string;
}

/** Register artifact signing verification tool on the MCP server. */
export function registerArtifactSigningTools(server: McpServer): void {
  server.tool(
    'stackwright_pro_verify_artifact_signatures',
    'Verify ECDSA P-384 signatures for all pipeline artifacts in .stackwright/artifacts/. ' +
      'Auto-discovers keys from .stackwright/pipeline-keys.json. ' +
      'Returns per-artifact verification status.',
    {
      cwd: z.string().optional().describe('Project root directory. Defaults to process.cwd().'),
    },
    async ({ cwd: cwdParam }) => {
      const cwd = cwdParam ?? process.cwd();

      // 1. Load pipeline keys
      let publicKey: KeyObject;
      try {
        const keys = loadPipelineKeys(cwd);
        publicKey = keys.publicKey;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify({
                error: true,
                message: `Cannot load pipeline keys: ${msg}`,
              }),
            },
          ],
          isError: true,
        };
      }

      // 2. Load signature manifest
      let manifest: SignatureManifest;
      try {
        manifest = loadSignatureManifest(cwd);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify({
                error: true,
                message: `Cannot load signature manifest: ${msg}`,
              }),
            },
          ],
          isError: true,
        };
      }

      // 3. Discover artifact files on disk
      const artifactsPath = join(cwd, ARTIFACTS_DIR);
      let artifactFiles: string[] = [];
      try {
        if (existsSync(artifactsPath)) {
          artifactFiles = readdirSync(artifactsPath).filter(
            (f) => f.endsWith('.json') && f !== SIGNATURE_MANIFEST
          );
        }
      } catch {
        // If we can't read the directory, we'll report zero artifacts
      }

      // 4. Verify each artifact
      const results: ArtifactVerificationResult[] = [];
      let hasFailure = false;

      for (const filename of artifactFiles) {
        const filePath = join(artifactsPath, filename);

        // Symlink guard
        try {
          rejectSymlink(filePath, `artifact ${filename}`);
        } catch {
          results.push({
            filename,
            verified: false,
            error: 'Refusing to verify symlink',
          });
          hasFailure = true;
          continue;
        }

        // Read artifact bytes
        let artifactBytes: Buffer;
        try {
          artifactBytes = readFileSync(filePath);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          results.push({
            filename,
            verified: false,
            error: `Cannot read artifact: ${msg}`,
          });
          hasFailure = true;
          continue;
        }

        // Look up signature in manifest
        const entry = manifest.signatures[filename];
        if (!entry) {
          results.push({
            filename,
            verified: false,
            error: 'No signature found in manifest',
          });
          hasFailure = true;
          continue;
        }

        // Verify
        const sig: ArtifactSignature = {
          digest: entry.digest,
          signature: entry.signature,
          algorithm: entry.algorithm,
          signedAt: entry.signedAt,
        };

        const verified = verifyArtifact(artifactBytes, sig, publicKey);

        if (!verified) {
          const actualDigest = computeSha384(artifactBytes);
          emitSignatureAuditEvent({
            artifactFilename: filename,
            expectedDigest: sig.digest,
            actualDigest,
            phase: entry.signedBy ?? 'unknown',
            timestamp: new Date().toISOString(),
            source: 'stackwright_pro_verify_artifact_signatures',
          });

          results.push({
            filename,
            verified: false,
            error: `Signature verification failed — artifact may have been tampered with`,
            signedBy: entry.signedBy,
            signedAt: entry.signedAt,
          });
          hasFailure = true;
        } else {
          results.push({
            filename,
            verified: true,
            signedBy: entry.signedBy,
            signedAt: entry.signedAt,
          });
        }
      }

      // 5. Report artifacts in manifest that are missing from disk
      for (const manifestFilename of Object.keys(manifest.signatures)) {
        if (!artifactFiles.includes(manifestFilename)) {
          results.push({
            filename: manifestFilename,
            verified: false,
            error: 'Artifact referenced in manifest but missing from disk',
          });
          hasFailure = true;
        }
      }

      const verifiedCount = results.filter((r) => r.verified).length;
      const failedCount = results.filter((r) => !r.verified).length;

      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify({
              totalArtifacts: artifactFiles.length,
              verifiedCount,
              failedCount,
              results,
              ...(hasFailure
                ? {
                    error:
                      'SIGNATURE VERIFICATION FAILED: One or more artifact signatures are invalid. ' +
                      'Do not proceed — artifacts may have been tampered with.',
                  }
                : {}),
            }),
          },
        ],
        isError: hasFailure,
      };
    }
  );
}
